import { useState } from 'react';
import {
  Dialog,
  useMediaQuery,
  IconButton,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import { requestDualAccount } from '../../services/advisor_services';
import { useAccount } from '../../contexts/Account';
import { Auth } from 'aws-amplify';
import { AccountType } from '../../services/utils/types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';

const useStyles = ({ isXS }) => ({
  dialog: {
    width: isXS ? null : '600px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dialogActions: {
    width: { xxs: '100%', sm: '400px' },
    padding: { xxs: '0px 24px', sm: '0px' },
    justifyContent: 'center',
    alignSelf: 'center',
  },
  mainButton: {
    padding: { xxs: '15px 30px 15px 30px', sm: '15px 30px 15px' },
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '14px',
    height: '40px',
    borderRadius: '40px',
    width: '100%',
  },
});

export default function DualAccountModal({ open, setOpen }) {
  const [isLoading, setIsLoading] = useState(false);
  const [boardName, setBoardName] = useState('');
  const { accountDetails, authenticate } = useAccount();
  const { executeRecaptcha } = useGoogleReCaptcha();

  function handleClose() {
    if (isLoading) {
      return;
    }
    setOpen(false);
  }

  async function handleDualAccountRequest() {
    setIsLoading(true);
    try {
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }
      const token = await executeRecaptcha('signUp');

      await requestDualAccount({
        companyName: boardName,
        email: accountDetails.email,
        givenName: accountDetails.givenName,
        phoneNumber: accountDetails.phoneNumber?.phoneNumber || '5555555555',
        phoneCountryCode: accountDetails.phoneNumber?.countryCode || 'US',
        recurlyPlanCode: 'dual_plan_code',
        surName: accountDetails.surName,
        urlOfCheckoutPage: window.location.href,
        postalCode: accountDetails.postalCode,
        recaptchaToken: token,
      });

      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        profile: AccountType.COMPANY,
      });
      await authenticate();
    } catch (error) {
      console.log({ error });
    }
  }

  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles({ isXS });

  return (
    <Dialog
      open={open}
      fullScreen={isXS}
      PaperProps={{
        sx: {
          borderRadius: { xxs: 0, sm: '10px' },
          padding: { xxs: '12px 0px 20px', sm: '16px 40px 24px' },
        },
      }}
      onClose={handleClose}
    >
      {isXS && (
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ position: 'absolute', top: 0, right: 0 }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <AdvisoryCloudLogo
          removeHyperlink
          onClick={() => {}}
          width={190}
          height={34}
        />
      </Box>
      <DialogContent sx={classes.dialog}>
        <Typography
          textAlign="center"
          pb={2}
          sx={{
            fontWeight: '400',
            fontSize: '14px',
            color: '#001030',
            paddingBottom: '24px',
          }}
        >
          Whether you're looking to invite your own business contacts or explore
          advisors in our network, you can now start your own Digital Boardroom
          for free. Share updates, get feedback, run polls, message advisors,
          and more.
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '-0.4300000071525574px',
            color: '#646D7A',
            width: { xxs: '100%', sm: '400px' },
            marginBottom: '12px',
            textAlign: 'center',
          }}
        >
          What would you like to name your advisory board?
        </Typography>
        <TextField
          id="dualAccountBoardNameStarter"
          placeholder="Company, team, or project name"
          variant="outlined"
          fullWidth
          sx={{
            width: { xxs: '100%', sm: '400px' },
          }}
          value={boardName}
          onChange={(e) => setBoardName(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          id="dualAccountCancelButton"
          color="primary"
          variant="outlined"
          onClick={() => {
            handleClose();
          }}
          sx={classes.mainButton}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          id="dualAccountCreateBoardButton"
          variant="contained"
          onClick={handleDualAccountRequest}
          sx={classes.mainButton}
          disabled={!boardName || isLoading}
        >
          {isLoading ? (
            <Box
              sx={{ width: '96px', display: 'flex', justifyContent: 'center' }}
            >
              <LoadingLogoSkeleton />
            </Box>
          ) : (
            'Create Board'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
